import { ProgramProps } from '../../pipeline-base';
import { CreateGlTextureByImageData, GetUniformLocation, SetTextureUniform } from '../../utils/pipeline-utils-gl';
import { createRegionMaskSamplerBindings } from '../../utils/shader-contract';

import { maskRegionGlslVec3PropString } from './regionMaskTypes';

/**
 * This class is created to try using components with pipelines, in lieu of making entirely new pipelines when a few small variations of what already exists,
 * need to be done.
 */
export class RegionMask {
  /**
   * The base texture will get this reference so it can be modified and updated throughout the draw-series process.
   * Perhaps rather than this, the draw series class needs to simply hold an "updated texture" reference... will refactor if needed, once it's all working.
   */
  public glTextureBaseRef?: WebGLTexture;
  public glTextureMask?: WebGLTexture;
  public scaleDownResult: boolean;

  public constructor() {
    this.scaleDownResult = false;
  }

  public static ModifyFragShaderText(fragShader: string, colourName: string): string {
    const colourProp = maskRegionGlslVec3PropString[colourName];

    // Add uniforms fields
    const fieldStr = `
uniform sampler2D uBaseTexture;
uniform sampler2D uRegionMask;
uniform int uScaleDownResult;
    `;

    fragShader = fragShader.replace(/\/\/ {{colourRegionMaskUniforms}}/gi, fieldStr);

    // Add uniform check to body
    const usageStr = `
  vec4 maskTexColour = texture2D(uRegionMask, outTexCoord);

  // MASK GRADIENT ACCEPTANCE

  if(maskTexColour.${colourProp} <= 0.001) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
    return;
  }

  // Set texColourToShade to the base colours (red-scale to be altered) to make sure the proper result is achieved.
  texColourToShade = texture2D(uBaseTexture, outTexCoord);
    `;

    fragShader = fragShader.replace(/\/\/ {{colourRegionMaskCheck}}/gi, usageStr);

    // "gl_FragColor" has most up-to-date colour after masking.
    const shadeStr = `
  // When the mask pixel is < 1, we want to scale back each of the values of the given colour, and add that result to whatever the output texture has in place.
  if(uScaleDownResult == 1 && maskTexColour.${colourProp} < 1.0) {
    vec4 maskColourChannelModdedOutput = gl_FragColor * maskTexColour.${colourProp};
    gl_FragColor = vec4(maskColourChannelModdedOutput);
  }
    `;

    fragShader = fragShader.replace(/\/\/ {{colourRegionMaskShade}}/gi, shadeStr);

    // * Remove block comments
    fragShader = fragShader.replace(/\/\*[\s\S]*?\*\//g, '');

    // * Remove line comments that do not follow a colon (to avoid URLs)
    fragShader = fragShader.replace(/([^:]|^)\/\/.*$/gm, '$1');

    return fragShader;
  }

  public CreateSetTexture(gl: WebGLRenderingContext, imgData: ImageData) {
    this.glTextureMask = CreateGlTextureByImageData(gl, imgData, gl.CLAMP_TO_EDGE);
  }

  public SetTextures(textureBaseRef: WebGLTexture, textureMask: WebGLTexture) {
    this.glTextureBaseRef = textureBaseRef;
    this.glTextureMask = textureMask;
  }

  public UpdateBaseTexture(textureBaseRef: WebGLTexture) {
    this.glTextureBaseRef = textureBaseRef;
  }

  public RemoveTexture() {
    this.glTextureMask = undefined;
  }

  public GetProgramProps(
    gl: WebGLRenderingContext,
    shaderProgramProps: ProgramProps,
    shaderProgram: WebGLProgram,
    mainSamplerCount: number,
    pipelineName: string
  ) {
    shaderProgramProps.u_baseTexture = GetUniformLocation(gl, shaderProgram, 'uBaseTexture', pipelineName);
    shaderProgramProps.u_regionMask = GetUniformLocation(gl, shaderProgram, 'uRegionMask', pipelineName);
    shaderProgramProps.u_scaleDownResult = GetUniformLocation(gl, shaderProgram, 'uScaleDownResult', pipelineName);

    const regionBindings = createRegionMaskSamplerBindings(mainSamplerCount);
    shaderProgramProps.u_regionMask_baseUnit = regionBindings[0].textureUnit;
    shaderProgramProps.u_regionMask_maskUnit = regionBindings[1].textureUnit;
  }

  public SetUniforms(gl: WebGLRenderingContext, mainSamplerCount: number, shaderProgramProps: ProgramProps) {
    if (!this.glTextureBaseRef || !this.glTextureMask) { return; }

    const regionBindings = createRegionMaskSamplerBindings(mainSamplerCount);
    SetTextureUniform(
      gl,
      gl.TEXTURE0 + regionBindings[0].textureUnit,
      this.glTextureBaseRef,
      shaderProgramProps.u_baseTexture,
      regionBindings[0].textureUnit
    );
    SetTextureUniform(
      gl,
      gl.TEXTURE0 + regionBindings[1].textureUnit,
      this.glTextureMask,
      shaderProgramProps.u_regionMask,
      regionBindings[1].textureUnit
    );
    gl.uniform1i(shaderProgramProps.u_scaleDownResult, Number(this.scaleDownResult));
  }
}
